import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Executor } from "../../services";
import { useTypedSelector } from "../../redux/store";
import { OrderPageExecutorColumnId } from "../../redux/reducers/Orders/Executors/tableSettings";
import {
	sortByNumberOrString,
	isTextMatchingLeadingStarsFilterHelper,
} from "../../utils";
import { getExecutorStatuses } from "../../components/OrderPageWidgets/Executors/constants";
import { ColumnId as ColumnIdFromTabExecutors } from "../../pages/MainPage/pages/Accounts/tabs/Executors/components/Table/columns";
import { getRoleDisplayName } from "../../assets/languages/langs";

import { getFullName, getPhone, isLimit, isOrder } from ".";

const useExecutorsSubHandler = (): useExecutorsSubHandler.Props => {
	enum ColumnIdAdditional {
		CallSignExecutor = "callSign",
		Name = "name",
		FullName = "fullName",
		Phone = "phone",
		Phones = "phones",
		ExecutorGroup = "executorGroup",
		Roles = "roles",
		Team = "team",
	}

	const { t } = useTranslation();
	const executorsSubscribe = useTypedSelector(
		(state) => state.globalState.executors.data.subscription.basic,
	);
	const executorRate = useTypedSelector(
		(state) => state.globalState.executorRate.data.subscription.basic,
	);
	const lang = useTypedSelector((state) => state.session.language);

	const EXECUTOR_STATUSES = useMemo(() => getExecutorStatuses(t), [t]);

	const getStatusData = useCallback(
		(rowData) => {
			const isWorking = rowData?.isWorking ?? false;

			const onOwnOrder =
				rowData?.executorToOrders?.find(
					(order) => order.source === "executor" && !order.closedAt,
				) ?? false;

			const hasDebt = rowData?.debt ?? false;

			let status = rowData?.status;

			if (isWorking) {
				if (hasDebt) {
					if (onOwnOrder) {
						status = "ownOrderDebt";
					} else {
						status = "availableDebt";
					}
				} else if (onOwnOrder) {
					status = "ownOrder";
				}
			} else {
				status = "notWorking";
			}

			const statusData = EXECUTOR_STATUSES[status];

			return statusData;
		},
		[EXECUTOR_STATUSES],
	);

	const getStatusBackgroundColor = useCallback(
		(rowData) => {
			const online = rowData?.online ?? false;

			const statusData = getStatusData(rowData);

			return online ? statusData?.color : statusData?.offlineColor;
		},
		[getStatusData],
	);

	const getStatusFontColor = useCallback(
		(rowData) => {
			const statusData = getStatusData(rowData);

			return statusData?.fontColor;
		},
		[getStatusData],
	);

	const getStatusName = useCallback(
		(rowData) => {
			const isYourOrderStatus = rowData?.executorToOrders?.find(
				(order) => order.source === "executor" && !order.closedAt,
			);

			if (rowData.debt) {
				if (rowData.status === "available") {
					return EXECUTOR_STATUSES.availableDebt.label;
				}
				if (rowData.closedBy) return EXECUTOR_STATUSES.closed.label;

				if (isYourOrderStatus) {
					return EXECUTOR_STATUSES.ownOrderDebt.label;
				}

				return `${EXECUTOR_STATUSES[rowData.status].label} ${
					t("orderPageWidgets.executors.modelTable.str200") ?? ""
				}`;
			}

			if (isYourOrderStatus) {
				return EXECUTOR_STATUSES.ownOrder.label;
			}
			return EXECUTOR_STATUSES[rowData.status].label;
		},
		[t, EXECUTOR_STATUSES],
	);

	const getValueStatus = useCallback(
		(rowData) => {
			const name = getStatusName(rowData);
			const color = getStatusFontColor(rowData);
			const bg = getStatusBackgroundColor(rowData);
			return `${name}${color}${bg}`;
		},
		[getStatusBackgroundColor, getStatusFontColor, getStatusName],
	);

	const applyOrderSort = useCallback(
		(
			retval: Executor.Model[],
			order: Executor.Order | undefined,
		): Executor.Model[] => {
			if (!isOrder(order)) return retval;

			const [[dataKey, sortType] = []] = Object.entries(order || {});
			const isSort =
				typeof sortType === "string" &&
				typeof dataKey === "string" &&
				["asc", "desc"].includes(sortType.toLowerCase());

			if (isSort) {
				const isAscending = sortType.toLowerCase() === "asc";
				const sortOrder = isAscending ? 1 : -1;

				if (
					dataKey === ColumnIdAdditional.ExecutorGroup ||
					dataKey === ColumnIdAdditional.Team
				) {
					return retval.sort((prev, next) => {
						const findGroupName = (model: Executor.Model) =>
							model.group?.name?.[lang] ?? "";

						const prevValue = `${findGroupName(prev)}` || "";
						const nextValue = `${findGroupName(next)}` || "";

						return (
							sortByNumberOrString(prevValue, nextValue) *
							sortOrder
						);
					});
				}

				if (
					dataKey === ColumnIdAdditional.Phone ||
					dataKey === ColumnIdAdditional.Phones
				) {
					return retval.sort((prev, next) => {
						const prevValue = getPhone(prev.person);
						const nextValue = getPhone(next.person);

						return (
							sortByNumberOrString(prevValue, nextValue, true) *
							sortOrder
						);
					});
				}

				if (
					dataKey === ColumnIdAdditional.Name ||
					dataKey === ColumnIdAdditional.FullName
				) {
					return retval.sort((prev, next) => {
						const prevValue = getFullName(prev.person);
						const nextValue = getFullName(next.person);

						return (
							sortByNumberOrString(prevValue, nextValue, true) *
							sortOrder
						);
					});
				}

				if (dataKey === OrderPageExecutorColumnId.ONLINE) {
					const sort = retval.sort((prev, next) => {
						const prevValue = `ONLINE${prev.online}`;
						const nextValue = `ONLINE${next.online}`;
						return (
							sortByNumberOrString(prevValue, nextValue) *
							sortOrder
						);
					});

					return sort;
				}

				if (
					dataKey === OrderPageExecutorColumnId.ALIAS ||
					dataKey ===
						ColumnIdAdditional.CallSignExecutor /* for executor */
				) {
					const sort = retval.sort((prev, next) => {
						const prevValue = prev.alias;
						const nextValue = next.alias;
						return (
							sortByNumberOrString(prevValue, nextValue) *
							sortOrder
						);
					});

					return sort;
				}

				if (
					dataKey === OrderPageExecutorColumnId.CallSign /* for car */
				) {
					const sort = retval.sort((prev, next) => {
						const prevValue =
							prev.executorToCrews[0]?.crew?.car?.callSign || "";
						const nextValue =
							next.executorToCrews[0]?.crew?.car?.callSign || "";
						return (
							sortByNumberOrString(prevValue, nextValue) *
							sortOrder
						);
					});

					return sort;
				}

				if (dataKey === OrderPageExecutorColumnId.EXECUTOR_GROUP) {
					const sort = retval.sort((prev, next) => {
						const prevValue = prev?.group?.name?.[lang] || "";
						const nextValue = next?.group?.name?.[lang] || "";
						return (
							sortByNumberOrString(prevValue, nextValue) *
							sortOrder
						);
					});

					return sort;
				}

				if (dataKey === OrderPageExecutorColumnId.COMPANY) {
					const sort = retval.sort((prev, next) => {
						const prevValue =
							prev?.taxiService?.company?.name?.[lang] || "";
						const nextValue =
							next?.taxiService?.company?.name?.[lang] || "";
						return (
							sortByNumberOrString(prevValue, nextValue) *
							sortOrder
						);
					});

					return sort;
				}

				if (dataKey === OrderPageExecutorColumnId.TAXI_SERVICE) {
					const sort = retval.sort((prev, next) => {
						const prevValue =
							prev?.taxiService?.settlement?.[lang] || "";
						const nextValue =
							next?.taxiService?.settlement?.[lang] || "";
						return (
							sortByNumberOrString(prevValue, nextValue) *
							sortOrder
						);
					});

					return sort;
				}

				if (dataKey === OrderPageExecutorColumnId.STATUS) {
					const sort = retval.sort((prev, next) => {
						const prevValue = getValueStatus(prev) || "";
						const nextValue = getValueStatus(next) || "";
						return (
							sortByNumberOrString(prevValue, nextValue) *
							sortOrder
						);
					});

					return sort;
				}

				if (dataKey === OrderPageExecutorColumnId.IS_WORKING) {
					const sort = retval.sort((prev, next) => {
						const prevValue = `${prev?.isWorking}` || "";
						const nextValue = `${next?.isWorking}` || "";
						return (
							sortByNumberOrString(prevValue, nextValue) *
							sortOrder
						);
					});

					return sort;
				}

				if (dataKey === ColumnIdFromTabExecutors.RegisteredAt) {
					const sort = retval.sort((prev, next) => {
						const prevValue = prev.createdAt
							? Date.parse(prev.createdAt) || 0
							: 0;
						const nextValue = next.createdAt
							? Date.parse(next.createdAt) || 0
							: 0;
						return (
							sortByNumberOrString(prevValue, nextValue) *
							sortOrder
						);
					});

					return sort;
				}

				if (dataKey === ColumnIdFromTabExecutors.ParkNumber) {
					const sort = retval.sort((prev, next) => {
						const findNumber = (model: Executor.Model) =>
							model.cars
								?.filter((c) => !!c.parkNumber)
								?.map((c) => c.parkNumber)
								?.join(", ") ?? "";

						const prevValue = `${findNumber(prev)}` || "";
						const nextValue = `${findNumber(next)}` || "";

						return (
							sortByNumberOrString(prevValue, nextValue) *
							sortOrder
						);
					});

					return sort;
				}

				if (dataKey === ColumnIdFromTabExecutors.Balance) {
					const sort = retval.sort((prev, next) => {
						const findMoney = (model: Executor.Model) =>
							model.paymentAccounts?.find(
								(a) => a.type === "main",
							)?.amount ?? 0;

						const prevValue = findMoney(prev);
						const nextValue = findMoney(next);

						return (
							sortByNumberOrString(prevValue, nextValue) *
							sortOrder
						);
					});

					return sort;
				}

				if (dataKey === ColumnIdFromTabExecutors.BonusBalance) {
					const sort = retval.sort((prev, next) => {
						const findMoney = (model: Executor.Model) =>
							model.paymentAccounts?.find(
								(a) => a.type === "bonus",
							)?.amount ?? 0;

						const prevValue = findMoney(prev);
						const nextValue = findMoney(next);

						return (
							sortByNumberOrString(prevValue, nextValue) *
							sortOrder
						);
					});

					return sort;
				}

				if (
					dataKey === ColumnIdFromTabExecutors.Role ||
					dataKey === ColumnIdAdditional.Roles
				) {
					const sort = retval.sort((prev, next) => {
						const findTextFullRole = (model: Executor.Model) =>
							model.roles
								?.map((role) =>
									getRoleDisplayName(role.name, lang),
								)
								?.filter(Boolean)
								?.join(", ") ?? "";

						const prevValue = `${findTextFullRole(prev)}` || "";
						const nextValue = `${findTextFullRole(next)}` || "";

						return (
							sortByNumberOrString(prevValue, nextValue) *
							sortOrder
						);
					});

					return sort;
				}

				if (
					dataKey === ColumnIdFromTabExecutors.ResponsibleDispatcher
				) {
					const sort = retval.sort((prev, next) => {
						const findTextFullName = (model: Executor.Model) =>
							[
								model.responsibleDispatcher?.person?.lastName ??
									"",
								model.responsibleDispatcher?.person
									?.firstName ?? "",
								model.responsibleDispatcher?.person
									?.fatherName ?? "",
							]
								?.join(" ")
								?.trim() ?? "";

						const prevValue = `${findTextFullName(prev)}` || "";
						const nextValue = `${findTextFullName(next)}` || "";

						return (
							sortByNumberOrString(prevValue, nextValue) *
							sortOrder
						);
					});

					return sort;
				}

				if (dataKey === ColumnIdFromTabExecutors.ExecutorRateId) {
					const sort = retval.sort((prev, next) => {
						const findTextRate = (id: number | undefined) =>
							executorRate?.find((rate) => rate.id === id)
								?.name ?? "";

						const prevValue =
							`${findTextRate(prev?.priority)}` || "";
						const nextValue =
							`${findTextRate(next?.priority)}` || "";
						return (
							sortByNumberOrString(prevValue, nextValue) *
							sortOrder
						);
					});

					return sort;
				}

				if (dataKey === ColumnIdFromTabExecutors.Priority) {
					const sort = retval.sort((prev, next) => {
						const prevValue = `${prev?.priority}` || "";
						const nextValue = `${next?.priority}` || "";
						return (
							sortByNumberOrString(prevValue, nextValue) *
							sortOrder
						);
					});

					return sort;
				}
			}

			return retval;
		},

		[
			ColumnIdAdditional.CallSignExecutor,
			ColumnIdAdditional.ExecutorGroup,
			ColumnIdAdditional.FullName,
			ColumnIdAdditional.Name,
			ColumnIdAdditional.Phone,
			ColumnIdAdditional.Phones,
			ColumnIdAdditional.Roles,
			ColumnIdAdditional.Team,
			executorRate,
			getValueStatus,
			lang,
		],
	);

	const getAllExecutorsSubHandler = useCallback(
		(
			{
				order,
				query,
				limit,
				taxiServiceIds,
				isOnline,
				workingStatus,
				statuses,
				hasCallSign,
			}: useExecutorsSubHandler.SmallSubscribeOptions,
			opt: {
				query?: {
					person?: {
						fatherName?: boolean;
						surname?: boolean;
						phones?: boolean;
						name?: boolean;
					};
				};
			} = {},
		): Executor.Model[] => {
			if (!Array.isArray(executorsSubscribe)) {
				return [];
			}

			let retval = [...(executorsSubscribe || [])];

			const [
				isQuery,
				_isOnline,
				isWorkingStatus,
				isStatuses,
				isTaxiServiceIds,
			] = [
				typeof query === "string" && query !== "",
				typeof isOnline === "boolean",
				typeof workingStatus === "boolean",
				Array.isArray(statuses) && statuses.length > 0,
				Array.isArray(taxiServiceIds) && taxiServiceIds?.length,
			];

			if (isQuery) {
				retval = retval.filter((item) => {
					if (
						query &&
						isTextMatchingLeadingStarsFilterHelper(
							item.alias,
							query,
						)
					) {
						return true;
					}

					if (query && opt?.query?.person && item?.person) {
						const pid = `
						${
							opt?.query?.person?.surname &&
							item?.person?.lastName
								? item.person.lastName
								: ""
						} ${
							opt?.query?.person?.name && item?.person?.firstName
								? item.person.firstName
								: ""
						} ${
							opt?.query?.person?.fatherName &&
							item?.person?.fatherName
								? item.person.fatherName
								: ""
						}`.trim();
						if (
							pid &&
							isTextMatchingLeadingStarsFilterHelper(pid, query)
						) {
							return true;
						}

						if (
							opt?.query?.person?.phones &&
							item?.person?.phones?.length &&
							item?.person?.phones.some((phone) =>
								isTextMatchingLeadingStarsFilterHelper(
									phone.number,
									query,
								),
							)
						) {
							return true;
						}
					}

					return false;
				});
			}

			if (_isOnline) {
				retval = retval.filter((item) => item.online === isOnline);
			}

			if (isWorkingStatus) {
				retval = retval.filter(
					(item) => item.isWorking === workingStatus,
				);
			}

			if (isStatuses) {
				retval = retval.filter(
					(item) =>
						statuses &&
						item.status &&
						statuses.includes(item.status),
				);
			}

			if (isTaxiServiceIds) {
				retval = retval.filter((item) => {
					if (
						item.taxiService?.id &&
						taxiServiceIds &&
						taxiServiceIds.includes(item.taxiService.id)
					)
						return true;

					if (
						taxiServiceIds &&
						item?.permits?.taxiServiceIds?.length &&
						taxiServiceIds.some((orderTSId) =>
							item.permits.taxiServiceIds.includes(orderTSId),
						)
					) {
						return true;
					}

					return false;
				});
			}

			if (typeof hasCallSign === "boolean") {
				retval = retval.filter((item) =>
					hasCallSign
						? !!item.alias && item.alias !== ""
						: !item.alias || item.alias === "",
				);
			}

			if (isOrder(order)) {
				retval = applyOrderSort(retval, order);
			}

			if (isLimit(limit)) {
				retval = retval.slice(0, limit);
			}

			return retval;
		},
		[applyOrderSort, executorsSubscribe],
	);

	// TODO DP TODO DP

	const getOrderAndLimitExecutorsSubHandler = useCallback(
		(
			data: useExecutorsSubHandler.SmallSubscribeOptions2,
			roleSubscribe: Executor.Model[],
		): Executor.Model[] => {
			const { limit, order } = data;

			if (!Array.isArray(roleSubscribe)) {
				return [];
			}

			let retval = [...(roleSubscribe || [])];

			// if (isOrder(order)) {
			// 	const [[dataKey, sortType] = []] = Object.entries(order || {});
			// 	const isSort =
			// 		typeof sortType === "string" &&
			// 		typeof dataKey === "string" &&
			// 		["asc", "desc"].includes(sortType.toLowerCase());

			// 	if (isSort) {
			// 		const isAscending = sortType.toLowerCase() === "asc";
			// 		const sortOrder = isAscending ? 1 : -1;

			// 		// if (dataKey === ColumnId.Name) {
			// 		// 	const sort = retval.sort((prev, next) => {
			// 		// 		const prevValue = prev.name?.[lang] ?? "";
			// 		// 		const nextValue = next.name?.[lang] ?? "";
			// 		// 		return (
			// 		// 			sortByNumberOrString(prevValue, nextValue) *
			// 		// 			sortOrder
			// 		// 		);
			// 		// 	});

			// 		// 	retval = sort;
			// 		// }

			// 		// if (dataKey === ColumnId.DriversAmount) {
			// 		// 	const sort = retval.sort((prev, next) => {
			// 		// 		const prevValue = prev.executorsQueue?.length ?? 0;
			// 		// 		const nextValue = next.executorsQueue?.length ?? 0;
			// 		// 		return (
			// 		// 			sortByNumberOrString(prevValue, nextValue) *
			// 		// 			sortOrder
			// 		// 		);
			// 		// 	});

			// 		// 	retval = sort;
			// 		// }

			// 		// if (dataKey === ColumnId.Aliases) {
			// 		// 	const sort = retval.sort((prev, next) => {
			// 		// 		const prevValue =
			// 		// 			prev.executorsQueue?.[0]?.executor?.callSign
			// 		// 				?.toString()
			// 		// 				?.toLowerCase() ?? "";
			// 		// 		const nextValue =
			// 		// 			next.executorsQueue?.[0]?.executor?.callSign
			// 		// 				?.toString()
			// 		// 				?.toLowerCase() ?? "";
			// 		// 		return (
			// 		// 			sortByNumberOrString(prevValue, nextValue) *
			// 		// 			sortOrder
			// 		// 		);
			// 		// 	});

			// 		// 	retval = sort;
			// 		// }
			// 	}
			// }

			if (isOrder(order)) {
				retval = applyOrderSort(retval, order);
			}

			if (isLimit(limit)) {
				retval = retval.slice(0, limit);
			}

			return retval;
		},
		[applyOrderSort],
	);

	return { getAllExecutorsSubHandler, getOrderAndLimitExecutorsSubHandler };
};

declare namespace useExecutorsSubHandler {
	interface Props {
		getAllExecutorsSubHandler: (
			data: SmallSubscribeOptions,
			opt?: {
				query?: {
					person?: {
						fatherName?: boolean;
						surname?: boolean;
						phones?: boolean;
						name?: boolean;
					};
				};
			},
		) => Executor.Model[];
		getOrderAndLimitExecutorsSubHandler: (
			data: SmallSubscribeOptions,
			roleSubscribe: Executor.Model[],
		) => Executor.Model[];
	}

	interface SmallSubscribeOptions {
		order?: Executor.Order;
		taxiServiceIds?: number[];
		query?: string;
		limit?: number;
		isOnline?: boolean;
		workingStatus?: boolean;
		statuses?: string[];
		hasCallSign?: boolean;
	}

	interface SmallSubscribeOptions2 {
		order?: Executor.Order;
		limit?: number;
	}
}

export default useExecutorsSubHandler;
